// src/utils/liveOutbox.ts
// ─────────────────────────────────────────────────────────────────────────────
//  Schránka „k odeslání“ – výsledky rozhodčího, které ještě server nepotvrdil
//  (docs/live-faze1-navrh.md, bod 3.6).
//
//  Zápis z telefonu bez signálu čeká ve frontě Firestore (IndexedDB) i přes
//  restart aplikace. Když ho server později zamítne (závod mezitím ukončen,
//  výsledek opravil hlavní rozhodčí, rozhodčí není v seznamu), SDK to po
//  obnovení stránky nijak neohlásí – výsledek by tiše zmizel. Každý zápis si
//  proto poznamená do localStorage. Záznam zmizí, jen když server zápis
//  potvrdí, když ho nahradí novější zadání téhož střelce a stanoviště, nebo
//  když rozhodčí potvrdí upozornění („Rozumím“). Zamítnutí ho nikdy nesmaže –
//  hláška u výsledku zmizí s obrazovkou (závod ukončen, rozhodčí odešel),
//  upozornění ne. Zbylé záznamy se porovnají se serverem a co se neuložilo,
//  se ukáže rozhodčímu. Zamítnutí se navíc ohlásí posluchačům schránky
//  (notifyOutboxRejected) – dozví se o něm i obrazovka rozhodčího otevřená
//  znovu poté, co ta, která zápis poslala, zmizela.
// ─────────────────────────────────────────────────────────────────────────────

import { getDocFromServer, waitForPendingWrites } from "firebase/firestore";
import { db } from "../firebase";
import { liveResultRef } from "./liveResults";
import { reportWarning } from "./reportError";

export interface OutboxEntry {
  raceId: string;
  startNumber: number;
  stationId: string;
  /** Body, které rozhodčí zadal. */
  score: number;
  /** uid rozhodčího (sdílené zařízení: každý vidí jen svoje). */
  uid: string;
  /** Kdy zadal (ms) – identifikuje zápis (novější zadání téhož nahrazuje starší). */
  at: number;
  /**
   * Rány a hlavička střelce (fáze 3, bod 5.4) – zápis, který se neodeslal
   * (fronta jen v paměti zmizela se zabitím aplikace), jde odeslat znovu.
   * Starší záznamy je nemají.
   */
  shots?: number[];
  gongValues?: number[];
  roundNumber?: number;
  userId?: string;
  sequenceNumber?: string;
  name?: string;
}

/** Neuložený výsledek: co rozhodčí zadal a co je na serveru (null = nic). */
export interface LostEntry extends OutboxEntry {
  serverScore: number | null;
}

/** Výsledek porovnání schránky se serverem (outboxReconcile). */
export interface OutboxReconcileResult {
  /** Neuložené výsledky – na serveru nic nebo jiná hodnota. */
  lost: LostEntry[];
  /**
   * Fronta zápisů se do limitu neodeslala: nic se neporovnalo a záznamy
   * zůstaly – volající porovná znovu, až fronta odejde (waitForPendingWrites).
   */
  timedOut: boolean;
}

const KEY = "lks_live_outbox_v1";
/** Záznamy starší než týden se zahodí (závod dávno vyřešený). */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * Nejdéle tak dlouho porovnání čeká na odeslání fronty zápisů; pak skončí
 * bez výsledku a záznamy nechá na příště (další otevření obrazovky, obnovení
 * připojení, ukončení závodu).
 */
export const RECONCILE_WAIT_MS = 20_000;

function readAll(): OutboxEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    const list = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    const now = Date.now();
    return list.filter(
      (e): e is OutboxEntry =>
        !!e &&
        typeof e.raceId === "string" &&
        typeof e.startNumber === "number" &&
        typeof e.stationId === "string" &&
        typeof e.score === "number" &&
        typeof e.uid === "string" &&
        typeof e.at === "number" &&
        now - e.at < MAX_AGE_MS
    );
  } catch {
    return [];
  }
}

function writeAll(list: OutboxEntry[]): void {
  try {
    if (list.length === 0) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // Soukromý režim / plné úložiště: schránka je jen pojistka
  }
}

const sameSlot = (a: OutboxEntry, b: Pick<OutboxEntry, "raceId" | "startNumber" | "stationId" | "uid">) =>
  a.raceId === b.raceId && a.startNumber === b.startNumber && a.stationId === b.stationId && a.uid === b.uid;

/** Poznamená zápis před odesláním (novější zadání téhož stanoviště a střelce nahradí starší). */
export function outboxAdd(entry: OutboxEntry): void {
  writeAll([...readAll().filter((e) => !sameSlot(e, entry)), entry]);
}

/** Server zápis potvrdil – odstraní ho (jen ten konkrétní zápis, novější zadání zůstane). */
export function outboxRemove(entry: Pick<OutboxEntry, "raceId" | "startNumber" | "stationId" | "uid" | "at">): void {
  writeAll(readAll().filter((e) => !(sameSlot(e, entry) && e.at === entry.at)));
}

/** Nepotvrzené zápisy rozhodčího v závodě. */
export function outboxList(raceId: string, uid: string): OutboxEntry[] {
  return readAll().filter((e) => e.raceId === raceId && e.uid === uid);
}

/** Odstraní dané záznamy (rozhodčí potvrdil „Rozumím“). */
export function outboxDiscard(entries: OutboxEntry[]): void {
  writeAll(readAll().filter((e) => !entries.some((x) => sameSlot(e, x) && e.at === x.at)));
}

// Posluchači zamítnutých zápisů (otevřená obrazovka rozhodčího). Zamítnutí
// ohlásí obrazovka, která zápis poslala (gongy, terče) – když mezitím zmizela
// (rozhodčí odešel šipkou zpět během „Ukládám…“ a obrazovku otevřel znovu),
// její vlastní porovnání už neběží; ohlásí ho proto i všem posluchačům.
// Každé přihlášení zvlášť (tatáž funkce přihlášená dvakrát = dva záznamy).
const rejectedListeners = new Set<{ listener: () => void }>();

/** Přihlásí posluchače zamítnutých zápisů výsledků; vrací odhlášení. */
export function onOutboxRejected(listener: () => void): () => void {
  const subscription = { listener };
  rejectedListeners.add(subscription);
  return () => {
    rejectedListeners.delete(subscription);
  };
}

/** Server zápis výsledku zamítl – ohlásí to všem přihlášeným posluchačům. */
export function notifyOutboxRejected(): void {
  Array.from(rejectedListeners).forEach(({ listener }) => {
    try {
      listener();
    } catch (err) {
      // Chyba jednoho posluchače nesmí zastavit ostatní ani zápis, který hlásí
      reportWarning("liveOutbox.rejectedListener", err);
    }
  });
}

/**
 * Odešla fronta zápisů na server do maxWaitMs? waitForPendingWrites se ve
 * vedlejší záložce (sdílená mezipaměť) nemusí vyřešit nikdy, když na odeslání
 * čeká zápis, který ta záložka nezná – bez limitu by porovnání viselo
 * a obrazovka rozhodčího by žádné další nespustila.
 */
async function pendingWritesSent(maxWaitMs: number): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), maxWaitMs);
  });
  try {
    return await Promise.race([waitForPendingWrites(db).then(() => true), timeout]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Počká, až fronta zápisů odejde na server (i zápisy z minulé relace; zamítnuté
 * se počítají jako vyřízené), a porovná zbylé záznamy se serverem:
 * - na serveru je stejná hodnota → uloženo (záznam se smaže),
 * - jinak (nic, nebo jiná hodnota – zápis zamítnut, nebo výsledek po uložení
 *   změnil hlavní rozhodčí; obojí vypadá stejně) → NEULOŽENO (záznam zůstane do
 *   potvrzení); rozhodčí uvidí, co zadal a co je na serveru, a nahlásí to
 *   hlavnímu rozhodčímu.
 * Offline / chyba čtení: lost = [] a záznamy zůstanou na příště. Fronta se do
 * maxWaitMs neodeslala: totéž s timedOut = true (volající porovná znovu, až
 * fronta odejde – jinak by zamítnutí, které přijde až po limitu a nikdo na jeho
 * zápis nečeká, zůstalo do dalšího otevření obrazovky neohlášené).
 */
export async function outboxReconcile(
  raceId: string,
  uid: string,
  maxWaitMs: number = RECONCILE_WAIT_MS
): Promise<OutboxReconcileResult> {
  const pending = outboxList(raceId, uid);
  if (pending.length === 0) return { lost: [], timedOut: false };
  try {
    if (!(await pendingWritesSent(maxWaitMs))) {
      reportWarning("liveOutbox.reconcileWait", new Error(`Fronta zápisů se do ${maxWaitMs} ms neodeslala`), {
        raceId,
        pending: pending.length,
      });
      return { lost: [], timedOut: true };
    }
    const lost: LostEntry[] = [];
    const settled: OutboxEntry[] = [];
    const bySn = new Map<number, OutboxEntry[]>();
    pending.forEach((e) => bySn.set(e.startNumber, [...(bySn.get(e.startNumber) || []), e]));
    for (const [sn, entries] of Array.from(bySn.entries())) {
      const snap = await getDocFromServer(liveResultRef(raceId, sn));
      const stations = (snap.exists() && (snap.data() as any).stations) || {};
      for (const e of entries) {
        const st = stations[e.stationId];
        const serverScore = st && typeof st.score === "number" ? st.score : null;
        // Jen stejná hodnota je uložená. Oprava hlavního rozhodčího (editedBy)
        // výsledek rozhodčího nevyřizuje – jeho zadání se mohlo lišit (třeba
        // novější přestřelení); upozornění ukáže obě hodnoty
        if (serverScore !== null && Math.abs(serverScore - e.score) < 0.005) {
          settled.push(e);
        } else {
          lost.push({ ...e, serverScore });
        }
      }
    }
    if (settled.length) outboxDiscard(settled);
    lost.sort((a, b) => a.startNumber - b.startNumber || a.stationId.localeCompare(b.stationId));
    return { lost, timedOut: false };
  } catch (err) {
    reportWarning("liveOutbox.reconcile", err, { raceId, pending: pending.length });
    return { lost: [], timedOut: false };
  }
}
